<template>
    <div>
        <div class="row mt-5">
            <div class="col-3 mb-2">
                <form method="post" @submit.prevent="onConnectLedger">
                    <label for="accountIndex">Account Index: (m/44'/60'/0'/0/x)</label>
                    <input type="number" min="0" class="form-control" id="accountIndex"
                        v-model.number="accountIndex" />
                    <button type="submit" class="btn btn-eth mt-1 w-100">Load from Ledger</button>
                </form>
            </div>

            <div class="col-4">
                <label>Address:</label>
                <p class="bold text-break">{{ ethAddress }}</p>
            </div>
            <div class="col-3">
                <label>HD Path:</label>
                <p class="bold">{{ hdPath }}</p>
            </div>
            <div class="col-2">
                <button class="btn btn-secondary btn-sm mt-3 mr-1" @click="logout">Logout</button>
                <button class="btn btn-primary btn-sm mt-3" @click="showAddAddressModal = true">Add Address</button>
                <button class="btn btn-outline-secondary btn-sm mt-2 w-100" @click="showReceive = true" v-if="ethAddress !== 'N/A'">📥 Receive</button>
            </div>
        </div>

        <!-- Balances + Address Book -->
        <div class="row">
            <div class="col-sm-7">
                <div class="total-usdt-banner" v-if="balances.length">
                    <span class="tub-label">Total Value</span>
                    <span class="tub-value">{{ totalUsdtStr }}</span>
                    <button class="tub-refresh" @click="loadBalances" :disabled="tokensLoading" title="Refresh USDT value">
                        <span v-if="tokensLoading" class="spinner-border spinner-border-sm" role="status"></span>
                        <span v-else>↻</span>
                    </button>
                </div>
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th>Asset</th>
                            <th>Balance</th>
                            <th>Value (USDT)</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="balances.length === 0 && !tokensLoading">
                            <td colspan="4" class="text-center text-muted">
                                Connect Ledger to load balances
                            </td>
                        </tr>
                        <tr v-if="tokensLoading">
                            <td colspan="4" class="text-center text-muted small py-1">
                                <span class="spinner-border spinner-border-sm mr-1" style="width:.75rem;height:.75rem"></span>
                                {{ pricingProgressMsg }}
                            </td>
                        </tr>
                        <tr v-for="b in sortedBalances" :key="b.contractAddress || b.ticker">
                            <td>
                                <span class="ticker-badge">{{ b.ticker }}</span>
                            </td>
                            <td>{{ b.balance }}</td>
                            <td>{{ b.usdValue != null ? '$' + b.usdValue.toFixed(2) : priceOf(b.ticker, b.rawBalance) }}</td>
                            <td>
                                <button class="btn btn-success btn-sm"
                                    @click="openModal(b.ticker, b.balance, b.type, b.contractAddress, b.decimals)">
                                    Send
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="col-sm-5">
                <table class="table table-striped text-center">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Address</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="addresses.length === 0">
                            <td colspan="3" class="text-muted">No saved addresses</td>
                        </tr>
                        <tr v-for="(entry, index) in addresses" :key="index">
                            <td>{{ entry.name }}</td>
                            <td class="small text-break">{{ entry.address }}</td>
                            <td>
                                <button class="btn btn-info btn-sm mx-1" @click="copyAddress(entry.address)">Copy</button>
                                <button class="btn btn-danger btn-sm" @click="deleteAddress(index)">Del</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- RPC Panel -->
        <div class="row mt-2">
            <div class="col-12">
                <div class="rpc-panel">
                    <span class="rpc-title">RPC Nodes</span>
                    <span v-for="rpc in rpcList" :key="rpc.url"
                        class="rpc-item"
                        :class="{ 'rpc-active': activeRpc === rpc.url }"
                        @click="switchRpc(rpc.url)">
                        <span class="rpc-dot" :class="rpcStatus[rpc.url]"></span>
                        {{ rpc.label }}
                    </span>
                    <span class="rpc-item" @click="pingAllRpc" style="cursor:pointer; margin-left:auto;">
                        ↻ Refresh
                    </span>
                </div>
            </div>
        </div>

        <!-- TX Log -->
        <div class="row mt-3" v-if="txLog.length">
            <div class="col-12">
                <div class="tx-log-panel">
                    <div class="tx-log-title">Transaction Log</div>
                    <div v-for="(entry, i) in txLog" :key="i" class="tx-log-entry" :class="entry.status">
                        <span class="tx-log-time">{{ entry.time }}</span>
                        <span class="tx-log-msg">{{ entry.msg }}</span>
                        <a v-if="entry.hash" :href="'https://etherscan.io/tx/' + entry.hash"
                            target="_blank" class="tx-log-hash">
                            {{ entry.hash.substring(0,12) }}…
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Transaction Chart ─────────────────────────────────────── -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-toolbar">
                        <span class="chart-panel-title">Transaction History</span>
                        <div class="chart-input-group">
                            <input type="text" class="form-control form-control-sm chart-addr-input"
                                placeholder="Ethereum address (0x…)"
                                v-model="chartAddress"
                                @keyup.enter="loadChart" />
                            <button class="btn btn-eth btn-sm" @click="loadChart" :disabled="chartLoading">
                                <span v-if="chartLoading">Loading…</span>
                                <span v-else>Load Chart</span>
                            </button>
                            <button v-if="ethAddress !== 'N/A'" class="btn btn-outline-secondary btn-sm"
                                @click="chartAddress = ethAddress; loadChart()">
                                My Address
                            </button>
                        </div>
                        <div class="chart-toggles">
                            <button class="btn btn-sm"
                                :class="chartMode==='count' ? 'btn-eth' : 'btn-outline-secondary'"
                                @click="setChartMode('count')">TX Count</button>
                            <button class="btn btn-sm"
                                :class="chartMode==='volume' ? 'btn-eth' : 'btn-outline-secondary'"
                                @click="setChartMode('volume')">ETH Volume</button>
                        </div>
                        <div class="chart-toggles">
                            <button v-for="d in [30,60,90]" :key="d" class="btn btn-sm"
                                :class="chartDays===d ? 'btn-eth' : 'btn-outline-secondary'"
                                @click="chartDays=d; renderChart()">{{ d }}d</button>
                        </div>
                    </div>
                    <div class="chart-summary" v-if="chartStats.total > 0">
                        <span class="chart-stat"><strong>{{ chartStats.total }}</strong> TXs</span>
                        <span class="chart-stat"><span class="stat-in">▲ {{ chartStats.received }} ETH received</span></span>
                        <span class="chart-stat"><span class="stat-out">▼ {{ chartStats.sent }} ETH sent</span></span>
                        <span class="chart-stat text-muted">Last {{ chartDays }} days</span>
                    </div>
                    <div class="chart-wrapper">
                        <canvas ref="txChart"></canvas>
                        <div v-if="!chartLoaded && !chartLoading" class="chart-placeholder">
                            Enter an Ethereum address above and click <strong>Load Chart</strong>
                        </div>
                        <div v-if="chartLoading" class="chart-placeholder">
                            <div class="spinner-border" style="color:#627EEA" role="status"></div>
                            <p class="mt-2 mb-0">Fetching transactions from Etherscan…</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TX Receipt Modal -->
        <Modal v-model="showReceipt" title="Transaction Sent">
            <div class="text-center p-3">
                <div class="tx-success-icon">✓</div>
                <p class="mt-2">Transaction submitted to Ethereum network.</p>
                <p class="small text-muted">Hash:</p>
                <input type="text" class="form-control form-control-sm mb-2" :value="txid" readonly />
                <a :href="'https://etherscan.io/tx/' + txid" target="_blank"
                    class="btn btn-eth btn-sm">
                    View on Etherscan ↗
                </a>
            </div>
        </Modal>

        <!-- Send Modal -->
        <Modal v-model="showModal" :title="'Send ' + modalTicker">
            <form novalidate @submit.prevent="sendTransaction">
                <div class="form-group">
                    <label>From</label>
                    <input type="text" class="form-control form-control-sm text-muted"
                        :value="ethAddress" readonly />
                </div>
                <div class="form-group">
                    <label for="inputToAddress">To Address</label>
                    <input id="inputToAddress" type="text" class="form-control"
                        placeholder="0x..." v-model="modalToAddress" />
                </div>
                <div class="form-group">
                    <label for="inputAmount">Amount ({{ modalTicker }})</label>
                    <div class="input-group">
                        <input id="inputAmount" type="text" class="form-control"
                            placeholder="0.0" v-model="modalAmount" />
                        <div class="input-group-append">
                            <button type="button" class="btn btn-outline-secondary btn-sm"
                                @click="modalAmount = modalMaxBalance">Max</button>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>Gas Price (Gwei)</label>
                    <input type="number" class="form-control" v-model.number="gasPrice" min="1" />
                </div>
                <div class="form-group">
                    <label>Gas Limit</label>
                    <input type="number" class="form-control" v-model.number="gasLimit" min="21000" />
                </div>
                <div class="form-group small text-muted">
                    Estimated fee:
                    <strong>{{ estimatedFee }} ETH</strong>
                </div>
                <div class="row modal-footer">
                    <div class="col-12">
                        <button class="btn btn-danger float-left" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-eth float-right" type="submit" :disabled="sending">
                            <span v-if="sending">Sending…</span>
                            <span v-else>Send</span>
                        </button>
                    </div>
                </div>
            </form>
        </Modal>

        <!-- Receive Modal -->
        <ReceiveModal v-if="showReceive" :address="ethAddress" chain="ethereum" @close="showReceive = false" />

        <!-- Add Address Modal -->
        <Modal v-model="showAddAddressModal" title="Add New Address">
            <form novalidate @submit.prevent="submitNewAddress">
                <div class="form-group">
                    <label>Name</label>
                    <input type="text" class="form-control" placeholder="Label" v-model="newAddressName" required />
                </div>
                <div class="form-group">
                    <label>Ethereum Address</label>
                    <input type="text" class="form-control" placeholder="0x..." v-model="newAddress" required />
                </div>
                <div class="row modal-footer">
                    <div class="col-12">
                        <button type="button" class="btn btn-danger float-left"
                            @click="showAddAddressModal = false">Cancel</button>
                        <button type="submit" class="btn btn-eth float-right">Save</button>
                    </div>
                </div>
            </form>
        </Modal>

        <!-- Address Matrix / Tree -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-panel-title mb-2">Address Overview</div>
                    <AddressMatrix
                        :cells="matrixCells"
                        :active-index="accountIndex"
                        accent-color="#627EEA"
                        unit="ETH"
                        :decimals="6"
                        :loading="matrixLoading"
                        root-label="m/44'/60'/0'/0"
                        @refresh="loadMatrix(true)"
                        @select="onMatrixSelect" />
                </div>
            </div>
        </div>

        <!-- Transaction History -->
        <WalletTxHistory chain="ethereum" :address="ethAddress" accent-color="#627EEA" />
    </div>
</template>

<script>
import Eth from '@ledgerhq/hw-app-eth';
import { Chart, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, BarController, LineController } from 'chart.js';
Chart.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, BarController, LineController);
import TransportWebHID from '@ledgerhq/hw-transport-webhid';
import TransportWebUSB from '@ledgerhq/hw-transport-webusb';
import { ethers } from 'ethers';
import numeral from 'numeral';
import BigNumber from 'bignumber.js';
import '@kouts/vue-modal/dist/vue-modal.css';
import axios from 'axios';
import { fetchPrices, formatUsd } from '../services/priceService';
import { saveAccount, getAccounts } from '../services/accountService';
import { getCustomTokens } from '../services/tokenService';
import { loadEvmTokens } from '../services/tokenLoader';
import { getAddresses, saveAddress, deleteAddress as deleteAddressPb } from '../services/pbService';
import { deriveEvmAddress, isValidHex } from '../services/evmXpub';
import AddressMatrix from './AddressMatrix.vue';
import eventBus from '../services/eventBus';

const EVM_NODE_KEY = 'hd_eth_account_node';
const MATRIX_ADDRESS_COUNT = 5;

// ─── RPC list ───────────────────────────────────────────────────────────────
const ETH_RPC_LIST = [
    { label: 'PublicNode',      url: 'https://ethereum-rpc.publicnode.com' },
    { label: 'Ankr',            url: 'https://rpc.ankr.com/eth' },
    { label: 'LlamaNodes',      url: 'https://eth.llamarpc.com' },
    { label: 'Ethereum.org',    url: 'https://ethereum.publicnode.com' },
];

// ─── Known ERC20 tokens ────────────────────────────────────────────────────
const ERC20_TOKENS = [
    { ticker: 'USDT',  address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6  },
    { ticker: 'USDC',  address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6  },
    { ticker: 'DAI',   address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', decimals: 18 },
    { ticker: 'WBTC',  address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', decimals: 8  },
    { ticker: 'LINK',  address: '0x514910771AF9Ca656af840dff83E8264EcF986CA', decimals: 18 },
    { ticker: 'UNI',   address: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', decimals: 18 },
    { ticker: 'SHIB',  address: '0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE', decimals: 18 },
    { ticker: 'PEPE',  address: '0x6982508145454Ce325dDbE47a25d4ec3d2311933', decimals: 18 },
];

const ERC20_ABI = [
    'function balanceOf(address) view returns (uint256)',
    'function decimals() view returns (uint8)',
    'function transfer(address to, uint256 amount) returns (bool)',
];

let provider = new ethers.providers.JsonRpcProvider(ETH_RPC_LIST[0].url);
let transport = null;
let ethApp = null;

export default {
    name: 'EthereumWallet',
    components: {
        Modal: () => import('@kouts/vue-modal').then(m => m.default),
        ReceiveModal: () => import('./ReceiveModal.vue'),
        WalletTxHistory: () => import('./WalletTxHistory.vue'),
        AddressMatrix,
    },
    data() {
        return {
            ethAddress: 'N/A',
            accountIndex: 0,
            hdPath: 'N/A',
            accountNode: null, // { pubkeyHex, chainCodeHex } at m/44'/60'/0'/0, used to derive the matrix/tree
            matrixCells: [],
            matrixLoading: false,
            balances: [],
            tokensLoading: false,
            pricingProgress: null, // {stage, current, total} — live status during the (sometimes slow) pricing step
            txLog: [],
            rpcList: ETH_RPC_LIST,
            activeRpc: ETH_RPC_LIST[0].url,
            rpcStatus: {},
            // send modal
            showModal: false,
            showReceipt: false,
            modalTicker: '',
            modalToAddress: '',
            modalAmount: '',
            modalMaxBalance: '',
            modalTxType: '',
            modalContractAddress: '',
            modalDecimals: 0,
            gasPrice: 20,
            gasLimit: 21000,
            sending: false,
            txid: '',
            // address book
            showAddAddressModal: false,
            newAddressName: '',
            newAddress: '',
            addresses: [],
            // pricing
            prices: {},
            priceTimer: null,
            showReceive: false,
            // chart
            chartAddress: '',
            chartMode: 'count',
            chartDays: 30,
            chartLoading: false,
            chartLoaded: false,
            chartInstance: null,
            chartStats: { total: 0, received: '0', sent: '0' },
        };
    },
    computed: {
        estimatedFee() {
            const fee = new BigNumber(this.gasPrice)
                .multipliedBy(this.gasLimit)
                .div(1e9);
            return fee.toFixed(8);
        },
        // Native ETH pinned to the top row, everything else sorted by USD value descending.
        sortedBalances() {
            const native = this.balances.filter(b => b.type === 'eth');
            const rest = this.balances.filter(b => b.type !== 'eth')
                .slice()
                .sort((a, b) => this.usdValueNum(b) - this.usdValueNum(a));
            return [...native, ...rest];
        },
        totalUsdt() {
            return this.balances.reduce((sum, b) => sum + Math.max(this.usdValueNum(b), 0), 0);
        },
        totalUsdtStr() {
            return formatUsd(this.totalUsdt) + ' USDT';
        },
        pricingProgressMsg() {
            const p = this.pricingProgress;
            if (!p) return 'Scanning all tokens…';
            if (p.stage === 'binance') return 'Pricing via Binance…';
            if (p.stage === 'coingecko') return `Pricing ${p.current}/${p.total} tokens via CoinGecko…`;
            return 'Scanning all tokens…';
        },
    },
    methods: {
        // ── Ledger ──────────────────────────────────────────────────────────
        async createTransport() {
            if (await TransportWebHID.isSupported()) return TransportWebHID.create();
            if (await TransportWebUSB.isSupported()) return TransportWebUSB.create();
            if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
                throw new Error('Page must be served over HTTPS for Ledger USB access.');
            }
            throw new Error('WebHID/WebUSB not supported. Please use Chrome or Edge.');
        },

        async onConnectLedger(e) {
            if (e) e.preventDefault();
            this.notice('info', 'Connecting', 'Opening Ledger connection…', 3);
            try {
                if (transport) await transport.close().catch(() => {});
                transport = await this.createTransport();
                ethApp = new Eth(transport);

                const path = `44'/60'/0'/0/${this.accountIndex}`;
                const { address } = await ethApp.getAddress(path, false, true);
                this.ethAddress = address;
                this.hdPath = 'm/' + path;
                this.addLog('info', `Address loaded: ${address}`);
                this.notice('success', 'Connected', 'Address loaded.');
                await this.loadBalances();
                await this.fetchGasPrice();
                const accounts = saveAccount({ chain: 'ethereum', hdIndex: this.accountIndex, address, label: `ETH #${this.accountIndex}` });
                if (this.$store) this.$store.commit('accounts/SET_ACCOUNTS', accounts);

                // Cache the account-level pubkey/chaincode (m/44'/60'/0'/0) so the
                // Address Matrix/Tree can derive other indices without reconnecting.
                try {
                    const { publicKey, chainCode } = await ethApp.getAddress("44'/60'/0'/0", false, true);
                    if (isValidHex(publicKey) && isValidHex(chainCode)) {
                        this.accountNode = { pubkeyHex: publicKey, chainCodeHex: chainCode };
                        localStorage.setItem(EVM_NODE_KEY, JSON.stringify(this.accountNode));
                    }
                } catch (nodeErr) {
                    console.error('Failed to cache ETH account node for matrix/tree:', nodeErr);
                }
                await this.loadMatrix();
            } catch (err) {
                console.error(err);
                const msg = err.message || String(err);
                const friendly = msg.includes('0x6e00') || msg.includes('CLA_NOT_SUPPORTED')
                    ? 'Open the Ethereum app on your Ledger, then try again.'
                    : msg;
                this.notice('error', 'Ledger Error', friendly, 10);
                this.addLog('error', 'Connect failed: ' + msg);
            }
        },

        // ── Balances ────────────────────────────────────────────────────────
        async loadBalances() {
            this.balances = [];
            this.notice('info', 'Loading', 'Fetching balances…', 3);

            // ETH
            try {
                const wei = await provider.getBalance(this.ethAddress);
                const eth = ethers.utils.formatEther(wei);
                this.balances.push({
                    ticker: 'ETH',
                    balance: numeral(parseFloat(eth)).format('0,0.000000000'),
                    type: 'eth',
                    rawBalance: eth,
                    contractAddress: null,
                    decimals: 18,
                });
                this.addLog('info', `ETH balance: ${eth}`);
            } catch (e) {
                this.notice('error', 'ETH Balance Error', e.message, 8);
            }

            // ERC20: discover all tokens with value > $0.01 (async, doesn't block native display)
            this.tokensLoading = true;
            this.pricingProgress = null;
            loadEvmTokens(this.ethAddress, 1, provider, 'ethereum', ERC20_TOKENS, p => { this.pricingProgress = p; })
                .then(tokens => {
                    // Remove any stale ERC20 rows, re-add freshly priced ones
                    this.balances = this.balances.filter(b => b.type !== 'erc20');
                    this.balances.push(...tokens);
                    if (this.$store) this.$store.commit('wallets/SET_WALLET', { chain: 'ethereum', data: { address: this.ethAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
                })
                .catch(e => this.notice('error', 'Token Load Error', e.message, 6))
                .finally(() => { this.tokensLoading = false; this.pricingProgress = null; });

            this.notice('success', 'Balances', 'ETH loaded — tokens loading…', 2);
            if (this.$store) {
                this.$store.commit('wallets/SET_WALLET', { chain: 'ethereum', data: { address: this.ethAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
            }
            await this.refreshPrices();
        },

        // ── Gas ─────────────────────────────────────────────────────────────
        async fetchGasPrice() {
            try {
                const gasPrice = await provider.getGasPrice();
                this.gasPrice = Math.ceil(parseFloat(ethers.utils.formatUnits(gasPrice, 'gwei')));
            } catch {
                // keep default
            }
        },

        // ── Send ────────────────────────────────────────────────────────────
        openModal(ticker, balance, type, contractAddress, decimals) {
            this.showModal = true;
            this.modalTicker = ticker;
            this.modalToAddress = '';
            this.modalAmount = '';
            this.modalMaxBalance = balance.replace(/,/g, '');
            this.modalTxType = type;
            this.modalContractAddress = contractAddress || '';
            this.modalDecimals = decimals || 18;
            this.gasLimit = type === 'erc20' ? 65000 : 21000;
            this.txid = '';
            this.showReceipt = false;
            this.fetchGasPrice();
        },

        closeModal() {
            this.showModal = false;
            this.sending = false;
        },

        isValidEthAddress(addr) {
            return ethers.utils.isAddress(addr);
        },

        async sendTransaction() {
            if (!this.isValidEthAddress(this.modalToAddress)) {
                this.notice('error', 'Invalid Address', 'Enter a valid 0x Ethereum address.', 5);
                return;
            }
            if (!this.modalAmount || parseFloat(this.modalAmount) <= 0) {
                this.notice('error', 'Invalid Amount', 'Amount must be greater than 0.', 5);
                return;
            }

            this.sending = true;
            const path = `44'/60'/0'/0/${this.accountIndex}`;

            try {
                const nonce = await provider.getTransactionCount(this.ethAddress, 'latest');
                const chainId = (await provider.getNetwork()).chainId;
                const gasPriceWei = ethers.utils.parseUnits(String(this.gasPrice), 'gwei');

                let txData;

                if (this.modalTxType === 'eth') {
                    const value = ethers.utils.parseEther(String(this.modalAmount));
                    txData = {
                        to: this.modalToAddress,
                        value: value.toHexString(),
                        gasPrice: gasPriceWei.toHexString(),
                        gasLimit: ethers.BigNumber.from(this.gasLimit).toHexString(),
                        nonce: ethers.utils.hexlify(nonce),
                        chainId,
                        data: '0x',
                    };
                } else {
                    // ERC20
                    const iface = new ethers.utils.Interface(ERC20_ABI);
                    const amount = new BigNumber(this.modalAmount)
                        .multipliedBy(new BigNumber(10).pow(this.modalDecimals))
                        .toFixed(0);
                    const data = iface.encodeFunctionData('transfer', [
                        this.modalToAddress,
                        ethers.BigNumber.from(amount),
                    ]);
                    txData = {
                        to: this.modalContractAddress,
                        value: '0x0',
                        gasPrice: gasPriceWei.toHexString(),
                        gasLimit: ethers.BigNumber.from(this.gasLimit).toHexString(),
                        nonce: ethers.utils.hexlify(nonce),
                        chainId,
                        data,
                    };
                }

                // Serialize for Ledger
                const unsignedTx = ethers.utils.serializeTransaction(txData);
                const txHex = unsignedTx.slice(2); // remove 0x

                this.notice('info', 'Confirm on Ledger', 'Approve the transaction on your Ledger device.', 60);
                this.addLog('info', `Awaiting Ledger confirmation for ${this.modalAmount} ${this.modalTicker} → ${this.modalToAddress}`);

                const sig = await ethApp.signTransaction(path, txHex, null);

                const _clientLog = (data) => {
                    const qs = Object.entries(data).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
                    fetch('/client-log?' + qs).catch(() => {});
                    console.log('[ETH-TRACE]', data);
                    this.addLog('info', Object.entries(data).map(([k,v]) => `${k}:${String(v).slice(0,30)}`).join(' | '));
                };

                _clientLog({
                    step: 'sig_raw',
                    v: sig.v, v_type: typeof sig.v,
                    r_type: typeof sig.r, r_isBuffer: Buffer.isBuffer(sig.r),
                    r_val: String(sig.r).slice(0, 40),
                    s_type: typeof sig.s, s_isBuffer: Buffer.isBuffer(sig.s),
                    s_val: String(sig.s).slice(0, 40),
                });

                const _sigToHex = (val) => {
                    if (Buffer.isBuffer(val)) return ethers.utils.hexlify(val);
                    if (typeof val === 'string' && val.includes(',')) return ethers.utils.hexlify(Buffer.from(val.split(',').map(Number)));
                    if (typeof val === 'string') return ethers.utils.hexlify(Buffer.from(val, 'hex'));
                    return ethers.utils.hexlify(val);
                };
                let rHex, sHex, vInt;
                try {
                    rHex = _sigToHex(sig.r);
                    sHex = _sigToHex(sig.s);
                    // Derive correct v by recovering address from both EIP-155 parities
                    const txHash = ethers.utils.keccak256(ethers.utils.serializeTransaction(txData));
                    vInt = chainId * 2 + 35;
                    for (const candidate of [chainId * 2 + 35, chainId * 2 + 36]) {
                        try {
                            const recovered = ethers.utils.recoverAddress(txHash, { r: rHex, s: sHex, v: candidate });
                            if (recovered.toLowerCase() === this.ethAddress.toLowerCase()) { vInt = candidate; break; }
                        } catch {}
                    }
                    _clientLog({ step: 'sig_hex', v: vInt, r: rHex, s: sHex });
                } catch (hexErr) {
                    _clientLog({ step: 'sig_hex_error', error: hexErr.message, v: sig.v, r: String(sig.r), s: String(sig.s) });
                    throw hexErr;
                }

                let signedTx;
                try {
                    signedTx = ethers.utils.serializeTransaction(txData, { v: vInt, r: rHex, s: sHex });
                    _clientLog({ step: 'serialize_ok', signedTx: signedTx.slice(0, 40) });
                } catch (serErr) {
                    _clientLog({ step: 'serialize_error', error: serErr.message, v: vInt, r: rHex, s: sHex });
                    throw serErr;
                }

                this.notice('info', 'Broadcasting', 'Sending to Ethereum network…', 10);
                const response = await provider.sendTransaction(signedTx);
                this.txid = response.hash;
                this.addLog('success', `TX sent: ${response.hash}`, response.hash);

                this.closeModal();
                this.showReceipt = true;
                this.notice('success', 'Sent!', `TX: ${response.hash.slice(0, 16)}…`, 5);

                // Wait for confirmation then refresh balances
                response.wait(1).then(() => {
                    this.addLog('success', `TX confirmed: ${response.hash}`, response.hash);
                    this.loadBalances();
                }).catch(err => {
                    this.addLog('error', 'TX failed on-chain: ' + err.message);
                });

            } catch (err) {
                console.error('TX error:', err);
                this.notice('error', 'Transaction Error', err.message || String(err), 10);
                this.addLog('error', 'TX failed: ' + (err.message || String(err)));
            } finally {
                this.sending = false;
            }
        },

        // ── Address Matrix / Tree ──────────────────────────────────────────
        async loadMatrix(notifyIfEmpty = false) {
            const cells = [];
            const savedAccounts = getAccounts().filter(a => a.chain === 'ethereum');
            for (let idx = 0; idx < MATRIX_ADDRESS_COUNT; idx++) {
                let address = null;
                if (this.accountNode) {
                    try {
                        address = deriveEvmAddress(this.accountNode.pubkeyHex, this.accountNode.chainCodeHex, idx);
                    } catch (e) {
                        console.error(`Failed to derive ETH address for index ${idx}:`, e);
                    }
                } else {
                    const saved = savedAccounts.find(a => a.hdIndex === idx);
                    if (saved) address = saved.address;
                }
                if (address) cells.push({ index: idx, address, balance: null, loading: true });
            }
            this.matrixCells = cells;
            if (!cells.length) {
                if (notifyIfEmpty) {
                    this.notice('error', 'Address Matrix Empty', 'No cached addresses/xpub found — reconnect your Ledger to populate it.', 8);
                }
                return;
            }
            this.matrixLoading = true;
            const results = await Promise.allSettled(
                cells.map(async c => ({ index: c.index, wei: await provider.getBalance(c.address) }))
            );
            const byIndex = {};
            for (const r of results) {
                if (r.status === 'fulfilled') byIndex[r.value.index] = parseFloat(ethers.utils.formatEther(r.value.wei));
                else console.error('[ETH matrix] balance fetch rejected:', r.reason);
            }
            this.matrixCells = this.matrixCells.map(c => ({ ...c, balance: byIndex[c.index] ?? 0, loading: !(c.index in byIndex) }));
            this.matrixLoading = false;
        },

        onMatrixSelect(idx) {
            const cell = this.matrixCells.find(c => c.index === idx);
            if (!cell) return;
            this.accountIndex = idx;
            this.ethAddress = cell.address;
            this.hdPath = `m/44'/60'/0'/0/${idx}`;
            this.loadBalances();
        },

        // ── RPC ─────────────────────────────────────────────────────────────
        async pingRpc(url) {
            this.$set(this.rpcStatus, url, 'checking');
            try {
                const p = new ethers.providers.JsonRpcProvider(url);
                await p.getBlockNumber();
                this.$set(this.rpcStatus, url, 'ok');
            } catch {
                this.$set(this.rpcStatus, url, 'error');
            }
        },

        switchRpc(url) {
            this.activeRpc = url;
            provider = new ethers.providers.JsonRpcProvider(url);
            this.pingRpc(url);
            this.addLog('info', `Switched RPC to ${url}`);
        },

        pingAllRpc() {
            this.rpcList.forEach(r => this.pingRpc(r.url));
        },

        // ── Address book ────────────────────────────────────────────────────
        async fetchAddresses() {
            try {
                const res = await getAddresses();
                this.addresses = res.items
                    .filter(r => r.chain === 'ethereum' || r.chain === 'all')
                    .map(r => ({ id: r.id, name: r.label, address: r.address }));
            } catch (e) {
                console.error('Failed to load addresses:', e);
            }
        },

        async submitNewAddress() {
            if (!this.isValidEthAddress(this.newAddress)) {
                alert('Enter a valid 0x Ethereum address.');
                return;
            }
            try {
                await saveAddress({
                    label: this.newAddressName,
                    address: this.newAddress,
                    chain: 'ethereum',
                    notes: '',
                    aml_risk_level: 'unknown',
                });
                await this.fetchAddresses();
            } catch (e) {
                alert('Failed to add address: ' + (e?.message || e));
                return;
            }
            this.newAddressName = '';
            this.newAddress = '';
            this.showAddAddressModal = false;
        },

        async deleteAddress(index) {
            const entry = this.addresses[index];
            try {
                if (entry.id) await deleteAddressPb(entry.id);
                this.addresses.splice(index, 1);
            } catch (e) {
                alert('Delete failed: ' + (e?.message || e));
            }
        },

        copyAddress(address) {
            navigator.clipboard.writeText(address).then(() => {
                this.notice('success', 'Copied', 'Address copied to clipboard.', 2);
            });
        },

        // ── TX Log ──────────────────────────────────────────────────────────
        addLog(status, msg, hash) {
            this.txLog.unshift({
                status,
                msg,
                hash: hash || null,
                time: new Date().toLocaleTimeString(),
            });
            if (this.txLog.length > 20) this.txLog.pop();
        },

        // ── Misc ────────────────────────────────────────────────────────────
        logout() {
            localStorage.removeItem('isLoggedIn');
            window.location.reload();
        },

        notice(type, title, description, duration) {
            this.$notice[type]({
                title,
                description: description || '',
                duration: duration ?? 1,
            });
        },

        // ── Pricing ──────────────────────────────────────────────────────────
        async refreshPrices() {
            const tickers = this.balances.map(b => b.ticker);
            const { prices } = await fetchPrices(tickers);
            this.prices = prices;
            if (this.$store) this.$store.dispatch('prices/fetchPrices', tickers);
        },

        priceOf(ticker, rawBalance) {
            const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[ticker] ?? null;
            if (price === null) return '—';
            return '$' + (raw * price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        // Numeric USD value for sorting — falls back to live price lookup like priceOf(),
        // but returns -1 (sorts last) when no value can be determined at all.
        usdValueNum(b) {
            if (b.usdValue != null) return b.usdValue;
            const raw = parseFloat(String(b.rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[b.ticker] ?? null;
            return price === null ? -1 : raw * price;
        },

        // ── Chart ─────────────────────────────────────────────────────
        setChartMode(mode) {
            this.chartMode = mode;
            if (this.chartLoaded) this.renderChart();
        },

        async loadChart() {
            const addr = this.chartAddress.trim();
            if (!this.isValidEthAddress(addr)) {
                this.notice('error', 'Invalid Address', 'Enter a valid 0x Ethereum address.', 4);
                return;
            }
            this.chartLoading = true;
            this.chartLoaded = false;
            try {
                // Etherscan public API — no key needed for low-volume use
                const key = process.env.VUE_APP_ETHERSCAN_KEY || '';
                const url = `https://api.etherscan.io/v2/api?chainid=1&module=account&action=txlist` +
                    `&address=${addr}&sort=desc&page=1&offset=200&startblock=0&endblock=99999999&apikey=${key}`;
                const res = await axios.get(url, { timeout: 15000 });
                const txs = Array.isArray(res.data.result) ? res.data.result : [];

                if (res.data.status === '0' && res.data.message !== 'No transactions found') {
                    throw new Error(res.data.result || 'Etherscan API error');
                }

                const now = Date.now();
                const msPerDay = 86400000;
                const cutoff = now - this.chartDays * msPerDay;

                const countMap = {}, sentMap = {}, recvMap = {};
                for (let d = 0; d < this.chartDays; d++) {
                    const day = this._dayKey(now - d * msPerDay);
                    countMap[day] = 0; sentMap[day] = 0; recvMap[day] = 0;
                }

                let totalCount = 0, totalSent = 0, totalRecv = 0;

                txs.forEach(tx => {
                    if (tx.isError === '1') return;
                    const ts = parseInt(tx.timeStamp) * 1000;
                    if (ts < cutoff) return;
                    const day = this._dayKey(ts);
                    if (!(day in countMap)) return;

                    countMap[day]++;
                    totalCount++;

                    const ethVal = parseFloat(ethers.utils.formatEther(tx.value || '0'));
                    if (tx.from.toLowerCase() === addr.toLowerCase()) {
                        sentMap[day] += ethVal; totalSent += ethVal;
                    }
                    if (tx.to.toLowerCase() === addr.toLowerCase()) {
                        recvMap[day] += ethVal; totalRecv += ethVal;
                    }
                });

                this.chartStats = {
                    total: totalCount,
                    sent: totalSent.toFixed(6),
                    received: totalRecv.toFixed(6),
                };
                this._chartData = { labels: Object.keys(countMap).sort(), countMap, sentMap, recvMap };
                this.chartLoaded = true;
                this.$nextTick(() => this.renderChart());
            } catch (e) {
                console.error(e);
                this.notice('error', 'Chart Error', e.message || String(e), 10);
            } finally {
                this.chartLoading = false;
            }
        },

        _dayKey(ms) {
            const d = new Date(ms);
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        },

        renderChart() {
            if (!this._chartData) return;
            const { labels, countMap, sentMap, recvMap } = this._chartData;
            if (this.chartInstance) { this.chartInstance.destroy(); this.chartInstance = null; }
            const ctx = this.$refs.txChart;
            if (!ctx) return;

            const shortLabels = labels.map(l => l.slice(5));
            let datasets;
            if (this.chartMode === 'count') {
                datasets = [{
                    label: 'TX Count',
                    data: labels.map(l => countMap[l]),
                    backgroundColor: 'rgba(98,126,234,0.75)',
                    borderColor: '#627EEA',
                    borderWidth: 1,
                    borderRadius: 3,
                }];
            } else {
                datasets = [
                    {
                        label: 'ETH Received',
                        data: labels.map(l => parseFloat(recvMap[l].toFixed(6))),
                        backgroundColor: 'rgba(40,167,69,0.75)',
                        borderColor: '#28a745',
                        borderWidth: 1, borderRadius: 3,
                    },
                    {
                        label: 'ETH Sent',
                        data: labels.map(l => parseFloat(sentMap[l].toFixed(6))),
                        backgroundColor: 'rgba(98,126,234,0.65)',
                        borderColor: '#627EEA',
                        borderWidth: 1, borderRadius: 3,
                    },
                ];
            }

            this.chartInstance = new Chart(ctx, {
                type: 'bar',
                data: { labels: shortLabels, datasets },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'top' } },
                    scales: {
                        x: {
                            stacked: this.chartMode === 'volume',
                            grid: { display: false },
                            ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 15 },
                        },
                        y: {
                            stacked: this.chartMode === 'volume',
                            beginAtZero: true,
                            grid: { color: 'rgba(0,0,0,0.05)' },
                        },
                    },
                },
            });
        },
    },

    async mounted() {
        await this.fetchAddresses();
        this.pingAllRpc();
        this.priceTimer = setInterval(() => this.refreshPrices(), 60000);
        eventBus.$on('refresh-prices', this.refreshPrices);
        const active = localStorage.getItem('hd_active_ethereum');
        if (active) {
            const accts = JSON.parse(localStorage.getItem('hd_accounts') || '[]');
            const a = accts.find(x => x.id === active);
            if (a) this.accountIndex = a.hdIndex;
        }
        try {
            const cached = JSON.parse(localStorage.getItem(EVM_NODE_KEY) || 'null');
            if (cached && isValidHex(cached.pubkeyHex) && isValidHex(cached.chainCodeHex)) {
                this.accountNode = cached;
            }
        } catch (e) {
            console.error('Failed to load cached ETH account node:', e);
        }
        this.loadMatrix();
    },
    beforeDestroy() {
        clearInterval(this.priceTimer);
        eventBus.$off('refresh-prices', this.refreshPrices);
        if (this.chartInstance) this.chartInstance.destroy();
    },
};
</script>

<style scoped>
/* ── ETH brand color ── */
.btn-eth {
    background: rgba(98,126,234,0.18) !important;
    border: 1px solid rgba(98,126,234,0.5) !important;
    color: #a5b4fc !important;
    font-weight: 600 !important;
}
.btn-eth:hover, .btn-eth:focus {
    background: rgba(98,126,234,0.32) !important;
    color: #fff !important;
}
.btn-primary {
    background: #627EEA !important;
    border-color: #627EEA !important;
    color: #fff !important;
}
.btn-primary:hover { background: #4a62cc !important; color: #fff !important; }
.table thead th {
    background: rgba(98,126,234,0.15) !important;
    color: #a5b4fc !important;
    border-bottom: 2px solid rgba(98,126,234,0.4) !important;
}
.ticker-badge {
    display: inline-block;
    background: rgba(98,126,234,0.2);
    color: #a5b4fc;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 8px;
    letter-spacing: 0.05em;
    border: 1px solid rgba(98,126,234,0.35);
}

/* ── RPC panel ── */
.rpc-panel {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    padding: 10px 14px;
    background: var(--surface, #111520);
    border-radius: 10px;
    border: 1px solid var(--border, #242a3a);
    margin-bottom: 4px;
}
.rpc-title {
    font-size: 0.7rem; font-weight: 700;
    color: var(--text-muted, #52597a);
    text-transform: uppercase; letter-spacing: 0.07em; margin-right: 4px;
}
.rpc-item {
    display: flex; align-items: center; gap: 5px;
    font-size: 0.78rem; font-weight: 500;
    padding: 4px 11px; border-radius: 20px; cursor: pointer;
    border: 1px solid var(--border, #242a3a);
    background: var(--surface-2, #171b28);
    color: var(--text, #c8cfe8);
    transition: all 0.18s;
}
.rpc-item:hover { border-color: #627EEA; background: rgba(98,126,234,0.08); }
.rpc-active { border-color: #627EEA !important; background: rgba(98,126,234,0.14) !important; color: #fff !important; font-weight: 700 !important; }
.rpc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--border-2, #2e3650); display: inline-block; flex-shrink: 0; }
.rpc-dot.ok       { background: #22c55e; }
.rpc-dot.error    { background: #f43f5e; }
.rpc-dot.checking { background: #f59e0b; }

/* ── TX Log ── */
.tx-log-panel {
    border: 1px solid var(--border, #242a3a);
    border-radius: 8px;
    padding: 10px 14px;
    background: var(--surface-2, #171b28);
    max-height: 200px;
    overflow-y: auto;
}
.tx-log-title {
    font-size: 0.7rem; font-weight: 700;
    color: var(--text-muted, #52597a);
    text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 6px;
}
.tx-log-entry {
    display: flex; align-items: center; gap: 8px;
    font-size: 0.78rem; padding: 4px 0;
    border-bottom: 1px solid var(--border, #242a3a);
}
.tx-log-entry.success .tx-log-msg { color: #22c55e; }
.tx-log-entry.error   .tx-log-msg { color: #f43f5e; }
.tx-log-entry.info    .tx-log-msg { color: var(--text, #c8cfe8); }
.tx-log-time  { color: var(--text-muted, #52597a); min-width: 70px; }
.tx-log-hash  { color: #627EEA; text-decoration: none; margin-left: auto; }
.tx-log-hash:hover { text-decoration: underline; }

/* ── Receipt modal ── */
.tx-success-icon { font-size: 3rem; color: #22c55e; }

/* ── Chart ── */
.btn-eth-chart { background: #627EEA !important; border-color: #627EEA !important; color: #fff !important; font-weight: 700 !important; }
.btn-eth-chart:hover { background: #4a62cc !important; }
.chart-panel { border: 1px solid var(--border, #242a3a); border-radius: 12px; padding: 18px; background: var(--surface, #111520); box-shadow: 0 4px 24px rgba(0,0,0,0.4); }
.chart-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.chart-panel-title { font-weight: 700; font-size: 0.86rem; color: var(--text, #c8cfe8); white-space: nowrap; margin-right: 6px; }
.chart-input-group { display: flex; gap: 6px; flex: 1; min-width: 200px; }
.chart-addr-input { flex: 1; font-size: 0.82rem; }
.chart-toggles { display: flex; gap: 4px; }
.chart-summary { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; font-size: 0.8rem; padding: 8px 12px; background: var(--surface-2, #171b28); border-radius: 8px; }
.chart-stat { display: flex; align-items: center; gap: 4px; }
.stat-in  { color: #22c55e; font-weight: 700; }
.stat-out { color: #627EEA; font-weight: 700; }
.chart-wrapper { position: relative; height: 280px; }
.chart-placeholder {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  color: var(--text-muted, #52597a); font-size: 0.88rem;
  background: rgba(11,13,20,0.88); border-radius: 10px;
}
</style>
